import { css } from "@emotion/css";
import { Callout } from "@octopusdeploy/design-system-components";
import { ScriptingLanguage } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { useEffect, useState } from "react";
import type { FoundGatewayInstance } from "~/areas/infrastructure/components/ArgoCDInstances/AddArgoCDInstanceDialog";
import type { ArgoCDInstanceConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstanceConfiguration";
import { GatewayDiscoveryProgress } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/components/GatewayDiscoveryProgress";
import { generateHelmCommand } from "~/areas/infrastructure/components/ArgoCDInstances/createArgoCDGatewayHelmCommand";
import { useArgoCDInstanceHelmCommandGeneratedDispatcher } from "~/areas/infrastructure/components/ArgoCDInstances/useArgoCDInstanceHelmCommandGeneratedDispatcher";
import type { UseBearerTokenReturnValue } from "~/areas/infrastructure/components/ArgoCDInstances/useBearerToken";
import { contentContainerStyles } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/Styles";
import type { Language, TextFormat } from "~/components/CodeEditor/CodeEditor";
import { CodeEditor } from "~/components/CodeEditor/CodeEditor";
import TimeFromNowLabel from "~/components/TimeLabels/TimeFromNowLabel";
type InstallationViewProps = {
    configuration: ArgoCDInstanceConfiguration;
    gateway?: FoundGatewayInstance;
    bearerToken: Exclude<UseBearerTokenReturnValue, "refresh">;
};
export const InstallationView = (props: InstallationViewProps) => {
    const { configuration, gateway, bearerToken } = props;
    const [hideSearch, setHideSearch] = useState(true);
    const dispatchArgoInstallationAnalyticsEvent = useArgoCDInstanceHelmCommandGeneratedDispatcher();
    const foundGateway = !!gateway;
    useEffect(() => {
        // Set a timeout to show the component after 2000 milliseconds (2s)
        const timeoutId = setTimeout(() => setHideSearch(false), 2000);
        dispatchArgoInstallationAnalyticsEvent("Argo CD Gateway Helm command generated", {
            gatewayName: configuration.name,
            spaceId: configuration.spaceId,
        });
        // Clean up the timeout when the component unmounts
        return () => {
            clearTimeout(timeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configuration.name, configuration.spaceId]);
    const [selectedLanguage, setSelectedLanguage] = useState<ScriptingLanguage | Language | TextFormat>(ScriptingLanguage.Bash);
    const helmCommand = generateHelmCommand(configuration, bearerToken.accessToken === null ? undefined : bearerToken.accessToken, selectedLanguage);
    const tokenExpiredAndHaveGivenUp = !foundGateway && bearerToken.hasExpired;
    const expiryTimeInfo = !foundGateway && bearerToken.expiry && (<>
            Your token expires{" "}
            <b>
                <TimeFromNowLabel time={bearerToken.expiry.toISOString()}/>
            </b>
            .
        </>);
    const helmCommandLineCount = helmCommand.split(/\r\n|\r|\n/).length;
    //adjust the height of the code editor based on the number of lines in the helm command
    const codeEditorContainer = css({
        // 1.20rem per line (line height) and roughly 3rem of padding/margins/scrollbars
        minHeight: `calc((${helmCommandLineCount} * 1.20rem) + 3rem)`,
    });
    return (<div className={contentContainerStyles}>
            {!tokenExpiredAndHaveGivenUp && (<>
                    <p>We've generated a custom Helm command for you which includes a short-lived bearer token for Octopus Server. {expiryTimeInfo}</p>
                    <p>Copy and run this Helm command in a terminal connected to your Kubernetes cluster.</p>
                </>)}
            {tokenExpiredAndHaveGivenUp && (<Callout title={"Your bearer token has expired"} type={"danger"}>
                    <p>Regenerate the token and copy the updated Helm command.</p>
                </Callout>)}
            <>
                <CodeEditor value={helmCommand} readOnly lineWrapping={false} showCopyButton showLineNumbers={false} language={selectedLanguage} allowFullScreen containerClassName={codeEditorContainer} settingsOverride={{ theme: "default", wordWrap: false }} onChange={noOp} scriptingLanguageSelectorOptions={{
            supportedLanguages: [ScriptingLanguage.PowerShell, ScriptingLanguage.Bash],
            onScriptingLanguageChanged: (syntax: ScriptingLanguage | Language | TextFormat) => setSelectedLanguage(syntax),
        }}/>
            </>
            {!hideSearch && !tokenExpiredAndHaveGivenUp && !foundGateway && <GatewayDiscoveryProgress name={configuration.name} isFound={foundGateway} bearerTokenExpired={bearerToken.hasExpired} gatewayHealthStatus={"Unknown"}/>}
        </div>);
};
